import { Printer, Layers, Box } from 'lucide-react';
import { StatCard } from '@/components/StatCard';
import { ActionItemsPanel, useActionItems } from '@/features/actionItems';
import { usePrinters } from '@/features/printers';
import { PrinterStatusPanel } from '@/features/printers/components';
import { useDashboardStats } from '@/features/stats';
import { RecentModelsPanel, useRecentModels } from '@/features/models';
import { JobSummaryPanel, useJobs } from '@/features/jobs';

export function DashboardPage() {
  const { data: actionItems, isLoading: isLoadingActionItems, error: actionItemsError } = useActionItems();
  const { data: printers, isLoading: isLoadingPrinters } = usePrinters();
  const { data: stats } = useDashboardStats();
  const { data: recentModels, isLoading: isLoadingRecentModels, error: recentModelsError } = useRecentModels();
  const { data: jobsData, isLoading: isLoadingJobs, error: jobsError } = useJobs({ limit: 100 });

  // Extract statistics
  const filamentCount = stats?.filaments.total ?? 0;
  const totalWeightKg = ((stats?.filaments.totalWeightGrams ?? 0) / 1000).toFixed(1);

  return (
    <div className="p-4">
      {/* Page header */}
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-foreground">Overview</h1>
        <p className="text-sm text-foreground-muted">Monitor your 3D printing inventory</p>
      </div>

      {/* Stats grid */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Printer className="h-4 w-4" />}
          label="Printers"
          value={String(printers?.length ?? 0)}
          href="/printers"
        />
        <StatCard
          icon={<Layers className="h-4 w-4" />}
          label="Filaments"
          value={String(filamentCount)}
          subValue={`${totalWeightKg} kg total`}
          href="/filaments"
        />
        <StatCard
          icon={<Box className="h-4 w-4" />}
          label="Total Prints"
          value="—"
          disabled
        />
        <StatCard
          icon={<Box className="h-4 w-4" />}
          label="Active Prints"
          value="0"
          disabled
        />
      </div>

      {/* Content panels */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Action Items */}
        <ActionItemsPanel
          actionItems={actionItems ?? []}
          isLoading={isLoadingActionItems}
          error={actionItemsError}
        />

        {/* Jobs Summary */}
        <JobSummaryPanel
          jobs={jobsData?.data ?? []}
          isLoading={isLoadingJobs}
          error={jobsError}
        />

        {/* Printer Status */}
        <PrinterStatusPanel
          printers={printers ?? []}
          isLoading={isLoadingPrinters}
        />

        {/* Recent Models */}
        <RecentModelsPanel
          models={recentModels?.items ?? []}
          isLoading={isLoadingRecentModels}
          error={recentModelsError}
        />
      </div>
    </div>
  );
}
